import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import {
  colorTokens,
  getSpacing,
  typographyTokens,
} from "../../../../designTokens";

const POPOVER_Z_INDEX = 99999;

function capitalizeFirstLetter(value: string): string {
  const s = value.trim();
  if (s.length === 0) return value;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface EllipsisWithTooltipProps {
  children: React.ReactNode;
  maxWidth?: number | string;
  outerStyle?: React.CSSProperties;
  /** Merged into the inner ellipsis span (e.g. smaller typography for secondary table cells). */
  innerStyle?: React.CSSProperties;
  tooltipContent?: React.ReactNode;
  /** When true, tooltip is shown only when the text is actually truncated (overflow with ellipsis). Default false. */
  showTooltipOnlyWhenOverflow?: boolean;
}

function checkIsOverflowing(el: HTMLSpanElement | null): boolean {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth;
}

export const EllipsisWithTooltip: React.FC<EllipsisWithTooltipProps> = ({
  children,
  maxWidth,
  outerStyle,
  innerStyle,
  tooltipContent,
  showTooltipOnlyWhenOverflow = false,
}) => {
  const [hovered, setHovered] = useState(false);
  const [popoverRect, setPopoverRect] = useState<{ top: number; left: number } | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentToDisplay = tooltipContent || children;

  const displayText =
    typeof children === "string"
      ? capitalizeFirstLetter(children)
      : typeof children === "number"
        ? String(children)
        : children;

  const handleMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setHovered(true);
  };

  const handleMouseLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => setHovered(false), 100);
  };

  const updateOverflow = () => {
    setIsOverflowing(checkIsOverflowing(innerRef.current));
  };

  useLayoutEffect(() => {
    updateOverflow();
  }, [children, tooltipContent, maxWidth, outerStyle, innerStyle]);

  useEffect(() => {
    if (!showTooltipOnlyWhenOverflow || !triggerRef.current) return;
    const el = triggerRef.current;
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showTooltipOnlyWhenOverflow]);

  useEffect(() => {
    if (hovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverRect({
        top: rect.top - 8,
        left: rect.left,
      });
    } else {
      setPopoverRect(null);
    }
  }, [hovered]);

  useEffect(() => () => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
  }, []);

  const showTooltip =
    hovered &&
    contentToDisplay &&
    popoverRect &&
    (!showTooltipOnlyWhenOverflow || isOverflowing);

  return (
    <span
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        maxWidth: maxWidth
          ? typeof maxWidth === "number"
            ? `${maxWidth}px`
            : maxWidth
          : "100%",
        ...outerStyle,
      }}
    >
      <span
        ref={innerRef}
        style={{
          fontFamily: typographyTokens["Body/Medium"].family,
          fontSize: `${typographyTokens["Body/Medium"].size}px`,
          lineHeight: 1,
          color: colorTokens["Text/Black"],
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          maxWidth: "100%",
          ...innerStyle,
        }}
      >
        {displayText}
      </span>
      {showTooltip &&
        createPortal(
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              position: "fixed",
              top: popoverRect.top,
              left: popoverRect.left,
              transform: "translateY(-100%)",
              backgroundColor: colorTokens["Background/White"],
              color: colorTokens["Text/Black"],
              border: `1px solid ${colorTokens["Borders/Light"]}`,
              borderRadius: getSpacing("8px"),
              boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
              padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
              zIndex: POPOVER_Z_INDEX,
              minWidth: "max-content",
              maxWidth: "480px",
              minHeight: "48px",
              maxHeight: "320px",
              overflowY: "auto",
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              lineHeight: 1.6,
              fontFamily: typographyTokens["Body/Small"].family,
              fontSize: `${typographyTokens["Body/Small"].size}px`,
              textAlign: "left",
            }}
          >
          {typeof contentToDisplay === "string" ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("4px"),
              }}
            >
              {contentToDisplay
                .split(/,\s*|;\s*|\n/)
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line, i) => (
                  <div key={i}>{capitalizeFirstLetter(line)}</div>
                ))}
            </div>
          ) : (
            <div style={{ whiteSpace: "pre-line" }}>{contentToDisplay}</div>
          )}
          </div>,
          document.body
        )}
    </span>
  );
};
